import type { dwrClassesById } from '@/data/classes';
import type { Character, MoveRef } from '@/schemas/character';
import { applyLevelUp, type LevelUpInput } from '@/services/levelUp';
import { buildAllSpellRefs, getActiveSpellGrantsForLevelUpMany } from '@/services/spellGrants';
import { buildJustAcquiredMoves } from './justAcquired';
import type { LevelUpDraft } from './types';

/**
 * Helper transazionali per applicare il level-up draft alla scheda. Estratti
 * da `LevelUpFlowPage.tsx` (PR 8 Fase 2) per rispettare `max-lines: 400`.
 * Pure functions senza dipendenze React.
 */

export interface ApplyOk {
  ok: true;
  updated: Character;
}
export interface ApplyErr {
  ok: false;
  error: string;
}

type ClassDef = NonNullable<(typeof dwrClassesById)[keyof typeof dwrClassesById]>;

export function tryApplyLevelUp(
  character: Character,
  classDef: ClassDef | undefined,
  draft: LevelUpDraft,
): ApplyOk | ApplyErr {
  if (!classDef) {
    return { ok: false, error: 'Classe non disponibile.' };
  }
  if (draft.pastTen === 'addio') {
    return tryApplyRetirement(character, classDef);
  }
  const incompleteError = checkDraftCompleteness(character, draft);
  if (incompleteError) return { ok: false, error: incompleteError };
  try {
    const justAcquired = buildJustAcquiredMoves(
      character.classId,
      draft.moveAdded,
      draft.grantedChoice,
      draft.multiclassPick,
    );
    const grants = getActiveSpellGrantsForLevelUpMany(
      character.classId,
      character.moves,
      justAcquired,
    );
    const spellsAdded = buildAllSpellRefs(grants, draft.spellSelections);
    const input = buildLevelUpInputFromDraft(draft, spellsAdded);
    const updated = applyLevelUp(character, input, classDef);
    return { ok: true, updated };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Errore imprevisto' };
  }
}

function tryApplyRetirement(character: Character, classDef: ClassDef): ApplyOk | ApplyErr {
  try {
    const placeholderMoveRef: MoveRef = { id: '__retirement__', selectedChoices: [] };
    const input: LevelUpInput = { moveAdded: placeholderMoveRef, pastTen: 'addio' };
    const updated = applyLevelUp(character, input, classDef);
    return { ok: true, updated };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Errore imprevisto' };
  }
}

function checkDraftCompleteness(character: Character, draft: LevelUpDraft): string | null {
  if (!draft.moveAdded) return 'Selezione incompleta. Torna agli step precedenti.';
  const isPostTen =
    draft.pastTen === 'apprendista' ||
    draft.pastTen === 'instancabile' ||
    character.pastTen === 'apprendista' ||
    character.pastTen === 'instancabile';
  if (!isPostTen && !draft.statIncreased) {
    return 'Selezione incompleta. Torna agli step precedenti.';
  }
  return null;
}

function buildLevelUpInputFromDraft(
  draft: LevelUpDraft,
  spellsAdded: readonly { id: string }[],
): LevelUpInput {
  if (!draft.moveAdded) {
    throw new Error('Selezione incompleta (chiamare tryApplyLevelUp invece di questo helper).');
  }
  const input: LevelUpInput = { moveAdded: draft.moveAdded };
  if (draft.statIncreased !== undefined) input.statIncreased = draft.statIncreased;
  if (draft.pastTen !== undefined) input.pastTen = draft.pastTen;
  if (draft.secondaryArchetypeId !== undefined) {
    input.secondaryArchetypeId = draft.secondaryArchetypeId;
  }
  // **FI-80 (2026-05-24)**: propaga il setup compilato della mossa-archetipo
  // del 2° archetipo (es. `mago.archetipo-umano` con 18 spell choices).
  if (draft.secondaryArchetypeSetup !== undefined) {
    input.secondaryArchetypeSetup = draft.secondaryArchetypeSetup;
  }
  if (spellsAdded.length > 0) input.spellsAdded = spellsAdded;
  // FI-46 PR-B+: propaga la mappa raw delle pick spell per audit/edit-mode.
  // (Salvata in `LevelUpRecord.spellGrantSelections` dal `buildLevelUpRecord`).
  if (Object.keys(draft.spellSelections).length > 0) {
    input.spellSelections = draft.spellSelections;
  }
  if (draft.grantedChoice !== undefined) input.grantedChoice = draft.grantedChoice;
  applyMulticlassPickToInput(input, draft);
  return input;
}

/**
 * FI-17 / FI-46 PR-B+: estrae la propagazione di `multiclassPick` + i
 * relativi `bundleSetups` per ridurre la complessità ciclomatica del
 * builder principale (eslint complexity ≤ 10).
 */
function applyMulticlassPickToInput(input: LevelUpInput, draft: LevelUpDraft): void {
  if (draft.multiclassPick === undefined) return;
  // **FI-81 #2 (2026-05-24)**: il draft espone `grantedOfTargetSetup` 1:1 con
  // `LevelUpInput.multiclassPick.grantedOfTargetSetup`. Spread diretto OK
  // (lo schema input accetta tutti i campi del draft come optional).
  input.multiclassPick = draft.multiclassPick;
  const bs = draft.multiclassPick.bundleSetups;
  if (bs !== undefined && Object.keys(bs).length > 0) {
    input.bundleSetups = bs;
  }
}
